import { AlertCircle, MapPin } from "lucide-react";
import React, { useRef, useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { ContactFormSchema } from "./contactFormSchema";
import { AddressUsageModal } from "./DataUsageModals";

interface AddressSelectorProps {
  control: Control<ContactFormSchema>;
  errors: FieldErrors<ContactFormSchema>;
  onChange: (address: string, zipCode: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const GOOGLE_MAPS_API_KEY = "AIzaSyBTO3fCm_GIGsMV4TltJPAngo3kpCVKQg0";

type AddrComp = google.maps.GeocoderAddressComponent;

const getComp = (comps: AddrComp[] | undefined, type: string) =>
  comps?.find((c) => c.types.includes(type));

const getStreetAddress = (comps: AddrComp[] | undefined) => {
  if (!comps) return "";

  const streetNumber = getComp(comps, "street_number")?.long_name ?? "";
  // short_name usually gives "E 15th St" instead of "East 15th Street"
  const route =
    getComp(comps, "route")?.short_name ??
    getComp(comps, "route")?.long_name ??
    "";

  // Optional extras you may want on the same line
  const premise = getComp(comps, "premise")?.long_name ?? ""; // e.g., "Building A"
  const subpremise = getComp(comps, "subpremise")?.long_name ?? ""; // e.g., "Apt 5"

  const main = [streetNumber, route].filter(Boolean).join(" ").trim();
  const extras = [premise, subpremise ? `#${subpremise}` : ""]
    .filter(Boolean)
    .join(", ");
  return [main, extras].filter(Boolean).join(", ");
};

function PlaceAutocomplete({
  control,
  errors,
  onFocus,
  onChange,
}: AddressSelectorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");
  const [missingZipCodeError, setMissingZipCodeError] = React.useState(false);

  React.useEffect(() => {
    if (!places || !inputRef.current) return;

    const options: google.maps.places.AutocompleteOptions = {
      fields: ["formatted_address", "address_components"],
      types: ["address"],
      componentRestrictions: { country: "US" },
    };

    const autocomplete = new places.Autocomplete(inputRef.current, options);

    const onPlaceChanged = () => {
      const place = autocomplete.getPlace();

      if (!place.formatted_address) return;

      const streetAddress = getStreetAddress(place.address_components);

      // Extract zip code from address components
      let zipCode = "";
      if (place.address_components) {
        for (const component of place.address_components) {
          if (component.types.includes("postal_code")) {
            zipCode = component.short_name;
            break;
          }
        }
      }

      // Show error if no ZIP code found in selected address
      if (!zipCode) {
        setMissingZipCodeError(true);
        // Still update the address field so user sees what they selected
        onChange(streetAddress, "");
        return;
      }

      setMissingZipCodeError(false);
      onChange(streetAddress, zipCode);
    };

    autocomplete.addListener("place_changed", onPlaceChanged);

    return () => {
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [places]);

  return (
    <>
      <label
        htmlFor="address"
        className="block text-sm font-medium text-gray-700"
      >
        Home Address <span className="text-red-500">*</span>
      </label>
      <div className="mt-1 relative rounded-md shadow-sm w-full">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-4 w-4 text-gray-400" />
        </div>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <input
              ref={inputRef}
              type="text"
              id="address"
              data-test-id="input-address"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              onFocus={onFocus}
              placeholder="Start typing your address..."
              className={`block w-full pl-10 border ${
                errors.address
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
              } rounded-md shadow-sm py-2 px-3 focus:outline-none text-sm transition-colors duration-200`}
            />
          )}
        />
      </div>
      {errors.address && (
        <div className="text-red-500 text-sm mt-1 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {errors.address.message}
        </div>
      )}
      {missingZipCodeError && (
        <div className="text-red-500 text-sm mt-1 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          Please select a complete street address (e.g., "123 Main St")
        </div>
      )}
    </>
  );
}

export function AddressSelector(props: AddressSelectorProps) {
  const [addressUsageModalOpen, setAddressUsageModalOpen] = useState(false);
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <div className="flex flex-col gap-1 items-start w-full">
        <PlaceAutocomplete {...props} />
        <button
          type="button"
          onClick={() => setAddressUsageModalOpen(true)}
          className="text-xs text-primary-600 hover:text-primary-500 hover:underline mt-1"
        >
          How we use your address
        </button>
        <AddressUsageModal
          isOpen={addressUsageModalOpen}
          onClose={() => setAddressUsageModalOpen(false)}
        />
      </div>
    </APIProvider>
  );
}
